import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Plus, Loader2, FolderArchive, Trash2, UserCog, Gavel, ScrollText,
  ClipboardList, HelpCircle, FileText,
} from "lucide-react";

const TIPOS = [
  { value: "judicial", label: "Judicial", Icon: Gavel },
  { value: "notarial", label: "Notarial", Icon: ScrollText },
  { value: "consulta", label: "Consulta", Icon: HelpCircle },
  { value: "tramite", label: "Trámite", Icon: FileText },
  { value: "otro", label: "Otro", Icon: ClipboardList },
];
const ESTADOS = [
  { value: "en_progreso", label: "En progreso", cls: "bg-[#F5EEE0] text-[#7A1E2E] border-[#C19B6C]/50" },
  { value: "activo", label: "Activo", cls: "bg-[#EEF6EE] text-[#1F5B2E] border-[#1F5B2E]/20" },
  { value: "pausado", label: "Pausado", cls: "bg-white text-[#3d2a2a] border-[#E5DFD3]" },
  { value: "cerrado", label: "Cerrado", cls: "bg-[#FBEDEF] text-[#7A1E2E] border-[#7A1E2E]/25" },
  { value: "archivado", label: "Archivado", cls: "bg-[#F5EEE0] text-[#6B5D57] border-[#E5DFD3]" },
];

const EMPTY = {
  titulo: "", tipo: "judicial", descripcion: "", fecha_inicio: "", fecha_cierre: "",
  estado: "en_progreso", abogado_asignado: "", numero_expediente: "", notas: "",
};

const inputCls = "h-11 bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]";
const labelCls = "text-[10px] uppercase tracking-[0.18em] text-[#6B5D57] font-semibold";

function tipoBadge(tipo) {
  const t = TIPOS.find((x) => x.value === tipo);
  const Icon = t?.Icon || ClipboardList;
  return (
    <Badge variant="outline" className="border-[#E5DFD3] bg-[#F5EEE0] text-[#3d2a2a] font-normal">
      <Icon className="h-3 w-3 mr-1" strokeWidth={1.8} />
      {t?.label || tipo}
    </Badge>
  );
}
function estadoBadge(estado) {
  const e = ESTADOS.find((x) => x.value === estado);
  return <Badge className={`${e?.cls || "bg-white"} font-normal border`}>{e?.label || estado}</Badge>;
}

export default function ArchivoTab({ clienteId }) {
  const { user } = useAuth();
  const canEdit = ["super_admin", "admin", "abogado"].includes(user?.role);
  const [abogados, setAbogados] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    api.get(`/clientes/${clienteId}/procesos`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Error cargando archivo"))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/abogados").then((r) => setAbogados(r.data.filter((a) => a.activo))).catch(() => {});
    // eslint-disable-next-line
  }, [clienteId]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p }); setDialogOpen(true); };

  const save = async () => {
    if (!form.titulo.trim()) { toast.error("El título es obligatorio"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/procesos/${editing.id}`, form);
        toast.success("Proceso actualizado");
      } else {
        await api.post(`/clientes/${clienteId}/procesos`, form);
        toast.success("Proceso agregado al archivo");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al guardar");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/procesos/${deleteTarget.id}`);
      toast.success("Proceso eliminado");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error al eliminar");
    }
  };

  return (
    <div className="space-y-5" data-testid="archivo-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#C25A5A] font-semibold">Archivo del cliente</div>
          <h3 className="mt-1 font-display text-xl text-[#1A0810]">Trabajos y procesos ({items.length})</h3>
        </div>
        {canEdit && (
          <Button onClick={openNew} data-testid="archivo-add-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
            <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Agregar proceso
          </Button>
        )}
      </div>

      <div className="rounded-xl bg-white border border-[#E5DFD3] overflow-hidden">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin text-[#7A1E2E] mx-auto" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <FolderArchive className="h-10 w-10 text-[#C19B6C] mx-auto" strokeWidth={1.3} />
            <p className="mt-3 text-sm text-[#6B5D57]">Aún no hay procesos registrados para este cliente.</p>
            {canEdit && (
              <Button onClick={openNew} className="mt-6 bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
                <Plus className="mr-2 h-4 w-4" strokeWidth={2} /> Registrar primer proceso
              </Button>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F5EEE0] hover:bg-[#F5EEE0] border-b border-[#E5DFD3]">
                <TableHead className="text-[#3d2a2a] font-semibold">Proceso</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Tipo</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Estado</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Fecha inicio</TableHead>
                <TableHead className="text-[#3d2a2a] font-semibold">Abogado</TableHead>
                {canEdit && <TableHead className="text-[#3d2a2a] font-semibold text-right">Acciones</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id} data-testid={`proceso-row-${p.id}`} className="border-b border-[#E5DFD3] hover:bg-[#FBF9F4]">
                  <TableCell>
                    <div className="text-sm font-medium text-[#1A0810]">{p.titulo}</div>
                    {p.numero_expediente && <div className="text-xs text-[#6B5D57] mt-0.5">Exp. {p.numero_expediente}</div>}
                    {p.descripcion && <div className="text-xs text-[#6B5D57] mt-1 line-clamp-2 max-w-md">{p.descripcion}</div>}
                  </TableCell>
                  <TableCell>{tipoBadge(p.tipo)}</TableCell>
                  <TableCell>{estadoBadge(p.estado)}</TableCell>
                  <TableCell><span className="text-sm text-[#3d2a2a]">{p.fecha_inicio || "—"}</span></TableCell>
                  <TableCell><span className="text-sm text-[#3d2a2a]">{p.abogado_asignado || "—"}</span></TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`proceso-edit-${p.id}`} className="h-8 w-8 hover:bg-[#F5EEE0]">
                          <UserCog className="h-4 w-4 text-[#3d2a2a]" strokeWidth={1.8} />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(p)} data-testid={`proceso-delete-${p.id}`} className="h-8 w-8 hover:bg-[#FBEDEF]">
                          <Trash2 className="h-4 w-4 text-[#7A1E2E]" strokeWidth={1.8} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#FBF9F4] border-[#E5DFD3]" data-testid="proceso-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-[#1A0810]">
              {editing ? "Editar proceso" : "Agregar proceso al archivo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className={labelCls}>Título del trabajo/proceso *</Label>
              <Input data-testid="proceso-form-titulo" value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} className={`${inputCls} mt-2`} placeholder="Sucesorio Rojas — Distribución de bienes" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className={labelCls}>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="proceso-form-tipo" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="proceso-form-estado" className={`${inputCls} mt-2`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ESTADOS.map((e) => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className={labelCls}>Nº de expediente</Label>
                <Input data-testid="proceso-form-expediente" value={form.numero_expediente} onChange={(e) => setForm({ ...form, numero_expediente: e.target.value })} className={`${inputCls} mt-2`} placeholder="26-000234-CI" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Fecha de inicio</Label>
                <Input data-testid="proceso-form-fecha-inicio" type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
              <div>
                <Label className={labelCls}>Fecha de cierre</Label>
                <Input data-testid="proceso-form-fecha-cierre" type="date" value={form.fecha_cierre} onChange={(e) => setForm({ ...form, fecha_cierre: e.target.value })} className={`${inputCls} mt-2`} />
              </div>
            </div>
            <div>
              <Label className={labelCls}>Abogado asignado</Label>
              {abogados.length > 0 ? (
                <Select value={form.abogado_asignado} onValueChange={(v) => setForm({ ...form, abogado_asignado: v })}>
                  <SelectTrigger data-testid="proceso-form-abogado" className={`${inputCls} mt-2`}><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {abogados.map((a) => <SelectItem key={a.id} value={a.nombre}>{a.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid="proceso-form-abogado" value={form.abogado_asignado} onChange={(e) => setForm({ ...form, abogado_asignado: e.target.value })} className={`${inputCls} mt-2`} placeholder="Nombre del abogado" />
              )}
            </div>
            <div>
              <Label className={labelCls}>Descripción</Label>
              <Textarea data-testid="proceso-form-descripcion" value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} className="mt-2 min-h-[80px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Detalles del proceso o trabajo realizado" />
            </div>
            <div>
              <Label className={labelCls}>Notas internas</Label>
              <Textarea data-testid="proceso-form-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="mt-2 min-h-[64px] bg-white border-[#E5DFD3] text-[#1A0810] focus-visible:ring-[#7A1E2E]" placeholder="Observaciones internas" />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="proceso-save-button" className="bg-[#2B0913] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Guardar cambios" : "Agregar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-[#FBF9F4] border-[#E5DFD3]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-[#1A0810]">Eliminar proceso</AlertDialogTitle>
            <AlertDialogDescription className="text-[#4a3838]">
              ¿Está seguro de eliminar <b>{deleteTarget?.titulo}</b>? Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#E5DFD3] bg-white hover:bg-[#F5EEE0]">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={del} data-testid="proceso-confirm-delete" className="bg-[#7A1E2E] hover:bg-[#4A0E1C] text-[#F1EDE4]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
